import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  LightBulbIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Define valid exercise types
export type SocialSciencesExerciseType = 
  | 'survey-design' 
  | 'data-interpretation' 
  | 'case-study-analysis' 
  | 'social-experiment' 
  | 'policy-assessment';

// Helper function to check if exercise type is valid
export const isValidSocialSciencesExerciseType = (type: string): type is SocialSciencesExerciseType => {
  const validTypes: SocialSciencesExerciseType[] = [
    'survey-design',
    'data-interpretation', 
    'case-study-analysis',
    'social-experiment',
    'policy-assessment'
  ];
  return validTypes.includes(type as SocialSciencesExerciseType);
};

interface SocialSciencesInteractiveProps {
  content: {
    id: string;
    title: string;
    description: string;
    type: string;
    interactiveData: {
      exerciseType: SocialSciencesExerciseType;
      instructions: string;
      scenario?: any;
      expectedResults?: any;
      hints?: string[];
    };
  };
  onComplete: (completed: boolean, score?: number) => void;
}

const SocialSciencesInteractive: React.FC<SocialSciencesInteractiveProps> = ({
  content,
  onComplete
}) => {
  const [inputs, setInputs] = useState<Record<string, any>>({});
  const [results, setResults] = useState<Record<string, any>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [currentHint, setCurrentHint] = useState(0);
  
  // Tracking states
  const [startTime] = useState(new Date());
  const [timeSpent, setTimeSpent] = useState(0);
  const [windowFocused, setWindowFocused] = useState(true);
  const [tabSwitches, setTabSwitches] = useState(0);
  const [inputInteractions, setInputInteractions] = useState(0);
  const [minimumEngagementTime] = useState(120); // 2 minutes for social sciences
  const [isEngaged, setIsEngaged] = useState(false);

  const { exerciseType, instructions, scenario, hints } = content.interactiveData;

  // Debug logging and validation for exercise type
  useEffect(() => {
    console.log('SocialSciencesInteractive Debug:', {
      exerciseType,
      contentType: content.type,
      interactiveData: content.interactiveData,
      isValidType: isValidSocialSciencesExerciseType(exerciseType || '')
    });

    if (exerciseType && !isValidSocialSciencesExerciseType(exerciseType)) {
      console.error(`Invalid social sciences exercise type: "${exerciseType}"`);
      toast.error(`Invalid exercise type: "${exerciseType}". Please check the configuration.`);
    }
  }, [exerciseType, content]);

  useEffect(() => {
    initializeExercise();
  }, [exerciseType]);

  // Window focus tracking
  useEffect(() => {
    if (isSubmitted) return;

    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => {
      setWindowFocused(false);
      setTabSwitches(prev => prev + 1);
      if (tabSwitches >= 2) {
        toast('⚠️ Stay focused on your research for accurate assessment.', {
          icon: '📊',
          duration: 3000,
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWindowFocused(false);
      } else {
        setWindowFocused(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSubmitted, tabSwitches]);

  // Time tracking
  useEffect(() => {
    if (isSubmitted) return;

    const interval = setInterval(() => {
      if (windowFocused) {
        setTimeSpent(prev => {
          const newTime = prev + 1;
          if (newTime >= minimumEngagementTime && !isEngaged) {
            setIsEngaged(true);
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowFocused, isSubmitted, minimumEngagementTime, isEngaged]);

  const initializeExercise = () => {
    if (!exerciseType) {
      console.warn('SocialSciencesInteractive: No exerciseType provided');
      setInputs({});
      return;
    }

    switch (exerciseType) {
      case 'survey-design':
        setInputs({
          researchQuestion: '',
          targetPopulation: '',
          questions: ['', '', '', ''],
          methodology: '',
          expectedChallenges: ''
        });
        break;
      case 'data-interpretation':
        setInputs({
          patterns: '',
          correlations: '',
          significance: '',
          conclusions: '',
          limitations: ''
        });
        break;
      case 'case-study-analysis':
        setInputs({
          keyIssues: ['', ''],
          stakeholders: ['', '', ''],
          analysis: '',
          recommendations: '',
          evidence: ''
        });
        break;
      case 'social-experiment':
        setInputs({
          hypothesis: '',
          variables: { independent: '', dependent: '', control: [''] },
          methodology: '',
          ethics: '',
          expectedResults: ''
        });
        break;
      case 'policy-assessment':
        setInputs({
          policyGoals: '',
          targetGroups: [''],
          impacts: { positive: [''], negative: [''] },
          effectiveness: '',
          improvements: ''
        });
        break;
      default:
        console.warn(`SocialSciencesInteractive: Unknown exercise type "${exerciseType}"`);
        setInputs({});
        break;
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setInputInteractions(prev => prev + 1);
  };

  const validateSubmission = () => {
    if (!isEngaged) {
      toast.error(`Please spend at least ${Math.ceil(minimumEngagementTime / 60)} minutes analyzing the material.`);
      return false;
    }

    const requiredFields = getRequiredFields();
    const missingFields = requiredFields.filter(field => {
      const value = inputs[field];
      return !value || (Array.isArray(value) ? value.every(v => !v) : !value.length);
    });
    
    if (missingFields.length > 0) {
      toast.error('Please complete all required fields before submitting.');
      return false;
    }

    return true;
  };

  const getRequiredFields = () => {
    switch (exerciseType) {
      case 'survey-design':
        return ['researchQuestion', 'targetPopulation', 'methodology'];
      case 'data-interpretation':
        return ['patterns', 'conclusions'];
      case 'case-study-analysis':
        return ['analysis', 'recommendations'];
      case 'social-experiment':
        return ['hypothesis', 'methodology'];
      case 'policy-assessment':
        return ['policyGoals', 'effectiveness'];
      default:
        return [];
    }
  };

  const evaluateSubmission = () => {
    switch (exerciseType) {
      case 'survey-design':
        return evaluateSurveyDesign();
      case 'data-interpretation':
        return evaluateDataInterpretation();
      case 'case-study-analysis':
        return evaluateCaseStudy();
      case 'social-experiment':
        return evaluateSocialExperiment();
      case 'policy-assessment':
        return evaluatePolicyAssessment();
      default:
        return { score: 0, feedback: 'Unknown exercise type' };
    }
  };

  const evaluateSurveyDesign = () => {
    let score = 0;
    const feedback = [];

    // Research question clarity (25 points)
    if (inputs.researchQuestion?.length > 30 && inputs.researchQuestion.includes('?')) {
      score += 25;
      feedback.push('✓ Clear research question formulated');
    } else {
      feedback.push('Research question should be specific and include a question mark');
    }

    // Target population (20 points)
    if (inputs.targetPopulation?.length > 20) {
      score += 20;
      feedback.push('✓ Target population clearly defined');
    }

    // Quality questions (30 points)
    const validQuestions = inputs.questions?.filter((q: string) => q.length > 15).length || 0;
    score += Math.min(30, validQuestions * 7.5);
    if (validQuestions >= 3) {
      feedback.push('✓ Good survey questions provided');
    }

    // Methodology (25 points)
    if (inputs.methodology?.length > 50) {
      score += 25;
      feedback.push('✓ Methodology well explained');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateDataInterpretation = () => {
    let score = 0;
    const feedback = [];

    // Pattern identification (25 points)
    if (inputs.patterns?.length > 40) {
      score += 25;
      feedback.push('✓ Data patterns identified');
    }

    // Correlation analysis (25 points)
    if (inputs.correlations?.length > 30) {
      score += 25;
      feedback.push('✓ Correlations analyzed');
    }

    // Statistical significance (25 points)
    if (inputs.significance?.length > 30) {
      score += 25;
      feedback.push('✓ Statistical significance addressed');
    }

    // Conclusions (25 points)
    if (inputs.conclusions?.length > 40) {
      score += 25;
      feedback.push('✓ Valid conclusions drawn');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateCaseStudy = () => {
    let score = 0;
    const feedback = [];

    // Key issues identification (30 points)
    const validIssues = inputs.keyIssues?.filter((issue: string) => issue.length > 15).length || 0;
    score += Math.min(30, validIssues * 15);
    if (validIssues >= 2) {
      feedback.push('✓ Key issues well identified');
    }

    // Stakeholder analysis (20 points)
    const validStakeholders = inputs.stakeholders?.filter((s: string) => s.length > 5).length || 0;
    score += Math.min(20, validStakeholders * 6.67);

    // Analysis depth (30 points)
    if (inputs.analysis?.length > 100) {
      score += 30;
      feedback.push('✓ Thorough analysis provided');
    }

    // Recommendations (20 points)
    if (inputs.recommendations?.length > 50) {
      score += 20;
      feedback.push('✓ Actionable recommendations given');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluateSocialExperiment = () => {
    let score = 0;
    const feedback = [];

    // Hypothesis (25 points)
    if (inputs.hypothesis?.length > 30 && (inputs.hypothesis.includes('if') || inputs.hypothesis.includes('predict'))) {
      score += 25;
      feedback.push('✓ Testable hypothesis formulated');
    }

    // Variable identification (25 points)
    const variables = inputs.variables || {};
    if (variables.independent && variables.dependent) {
      score += 25;
      feedback.push('✓ Variables properly identified');
    }

    // Methodology (25 points)
    if (inputs.methodology?.length > 50) {
      score += 25;
      feedback.push('✓ Experimental methodology described');
    }

    // Ethics consideration (25 points)
    if (inputs.ethics?.length > 30) {
      score += 25;
      feedback.push('✓ Ethical considerations addressed');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const evaluatePolicyAssessment = () => {
    let score = 0;
    const feedback = [];

    // Policy goals (25 points)
    if (inputs.policyGoals?.length > 40) {
      score += 25;
      feedback.push('✓ Policy goals clearly articulated');
    }

    // Impact analysis (35 points)
    const impacts = inputs.impacts || {};
    const positiveImpacts = impacts.positive?.filter((p: string) => p.length > 10).length || 0;
    const negativeImpacts = impacts.negative?.filter((n: string) => n.length > 10).length || 0;
    
    if (positiveImpacts >= 1 && negativeImpacts >= 1) {
      score += 35;
      feedback.push('✓ Balanced impact analysis');
    }

    // Effectiveness assessment (25 points)
    if (inputs.effectiveness?.length > 50) {
      score += 25;
      feedback.push('✓ Policy effectiveness evaluated');
    }

    // Improvement suggestions (15 points)
    if (inputs.improvements?.length > 30) {
      score += 15;
      feedback.push('✓ Constructive improvements suggested');
    }

    return { score, feedback: feedback.join('\n') };
  };

  const handleSubmit = () => {
    if (!validateSubmission()) return;

    const evaluation = evaluateSubmission();
    const finalScore = Math.max(0, Math.min(100, evaluation.score));
    const passed = finalScore >= 70;

    setResults({
      score: finalScore,
      feedback: evaluation.feedback,
      passed,
      timeSpent,
      interactions: inputInteractions
    });

    setIsSubmitted(true);
    setIsCorrect(passed);
    
    onComplete(passed, finalScore);

    if (passed) {
      toast.success(`Excellent research! Score: ${finalScore}%`);
    } else {
      toast.error(`Score: ${finalScore}%. Review the feedback and try again.`);
    }
  };

  const getExerciseIcon = () => {
    switch (exerciseType) {
      case 'survey-design': return <ClipboardDocumentCheckIcon className="h-6 w-6" />;
      case 'data-interpretation': return <ChartBarIcon className="h-6 w-6" />;
      case 'case-study-analysis': return <DocumentTextIcon className="h-6 w-6" />;
      case 'social-experiment': return <BeakerIcon className="h-6 w-6" />;
      case 'policy-assessment': return <ArrowTrendingUpIcon className="h-6 w-6" />;
      default: return <UserGroupIcon className="h-6 w-6" />;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderExerciseContent = () => {
    switch (exerciseType) {
      case 'survey-design':
        return renderSurveyDesign();
      case 'data-interpretation':
        return renderDataInterpretation();
      case 'case-study-analysis':
        return renderCaseStudy();
      case 'social-experiment':
        return renderSocialExperiment();
      case 'policy-assessment':
        return renderPolicyAssessment();
      default:
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h4 className="font-semibold text-red-800 mb-2">❌ Unknown Exercise Type</h4>
            <p className="text-red-700 mb-3">
              Exercise type "{exerciseType}" is not recognized. Please check the configuration.
            </p>
            <div className="bg-red-100 p-3 rounded text-sm">
              <strong>Supported exercise types:</strong>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li>survey-design</li>
                <li>data-interpretation</li>
                <li>case-study-analysis</li>
                <li>social-experiment</li>
                <li>policy-assessment</li>
              </ul>
              <p className="mt-2">
                <strong>Current value:</strong> "{exerciseType}"
              </p>
            </div>
          </div>
        );
    }
  };

  const renderSurveyDesign = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Research Question *
        </label>
        <input
          type="text"
          value={inputs.researchQuestion || ''}
          onChange={(e) => handleInputChange('researchQuestion', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          placeholder="What specific question are you trying to answer?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Target Population *
        </label>
        <textarea
          value={inputs.targetPopulation || ''}
          onChange={(e) => handleInputChange('targetPopulation', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Describe your target demographic and sampling method"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Survey Questions
        </label>
        {[0, 1, 2, 3].map((index) => (
          <input
            key={index}
            type="text"
            value={inputs.questions?.[index] || ''}
            onChange={(e) => {
              const newQuestions = [...(inputs.questions || ['', '', '', ''])];
              newQuestions[index] = e.target.value;
              handleInputChange('questions', newQuestions);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
            placeholder={`Question ${index + 1}: Write a clear, unbiased survey question`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Methodology *
        </label>
        <textarea
          value={inputs.methodology || ''}
          onChange={(e) => handleInputChange('methodology', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="How will you collect and analyze the data? What tools will you use?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Expected Challenges
        </label>
        <textarea
          value={inputs.expectedChallenges || ''}
          onChange={(e) => handleInputChange('expectedChallenges', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="What challenges might you encounter and how will you address them?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderDataInterpretation = () => (
    <div className="space-y-6">
      {scenario?.chartUrl && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Data Visualization:</h4>
          <img 
            src={scenario.chartUrl} 
            alt="Data chart for analysis"
            className="max-w-full h-auto rounded-lg"
          />
        </div>
      )}

      {scenario?.dataTable && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Data Table:</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  {scenario.dataTable.headers?.map((header: string, i: number) => (
                    <th key={i} className="px-3 py-2 text-left font-medium">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scenario.dataTable.rows?.map((row: string[], i: number) => (
                  <tr key={i} className="border-t">
                    {row.map((cell, j) => (
                      <td key={j} className="px-3 py-2">{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Patterns & Trends *
        </label>
        <textarea
          value={inputs.patterns || ''}
          onChange={(e) => handleInputChange('patterns', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="What patterns or trends do you observe in the data?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Correlations
        </label>
        <textarea
          value={inputs.correlations || ''}
          onChange={(e) => handleInputChange('correlations', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Are there any correlations between variables? Describe them."
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Statistical Significance
        </label>
        <textarea
          value={inputs.significance || ''}
          onChange={(e) => handleInputChange('significance', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Comment on the statistical significance of your findings"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Conclusions *
        </label>
        <textarea
          value={inputs.conclusions || ''}
          onChange={(e) => handleInputChange('conclusions', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="What conclusions can you draw from this data? What are the implications?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Limitations
        </label>
        <textarea
          value={inputs.limitations || ''}
          onChange={(e) => handleInputChange('limitations', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="What are the limitations of this data or analysis?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderCaseStudy = () => (
    <div className="space-y-6">
      {scenario?.caseDescription && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Case Study:</h4>
          <div className="text-sm text-gray-700 leading-relaxed">
            {scenario.caseDescription}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Key Issues
        </label>
        {[0, 1].map((index) => (
          <textarea
            key={index}
            value={inputs.keyIssues?.[index] || ''}
            onChange={(e) => {
              const newIssues = [...(inputs.keyIssues || ['', ''])];
              newIssues[index] = e.target.value;
              handleInputChange('keyIssues', newIssues);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
            rows={2}
            placeholder={`Key Issue ${index + 1}: Identify a major problem or challenge`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Stakeholders
        </label>
        {[0, 1, 2].map((index) => (
          <input
            key={index}
            type="text"
            value={inputs.stakeholders?.[index] || ''}
            onChange={(e) => {
              const newStakeholders = [...(inputs.stakeholders || ['', '', ''])];
              newStakeholders[index] = e.target.value;
              handleInputChange('stakeholders', newStakeholders);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
            placeholder={`Stakeholder ${index + 1}: Who is affected by this case?`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Analysis *
        </label>
        <textarea
          value={inputs.analysis || ''}
          onChange={(e) => handleInputChange('analysis', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={4}
          placeholder="Provide a detailed analysis of the situation, causes, and contributing factors"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Recommendations *
        </label>
        <textarea
          value={inputs.recommendations || ''}
          onChange={(e) => handleInputChange('recommendations', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="What specific actions would you recommend to address the issues?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Supporting Evidence
        </label>
        <textarea
          value={inputs.evidence || ''}
          onChange={(e) => handleInputChange('evidence', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="What evidence supports your analysis and recommendations?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderSocialExperiment = () => (
    <div className="space-y-6">
      {scenario?.researchContext && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Research Context:</h4>
          <div className="text-sm text-gray-700">
            {scenario.researchContext}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Hypothesis *
        </label>
        <textarea
          value={inputs.hypothesis || ''}
          onChange={(e) => handleInputChange('hypothesis', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="If... then... (state your testable hypothesis)"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Variables
        </label>
        <div className="space-y-3">
          <input
            type="text"
            value={inputs.variables?.independent || ''}
            onChange={(e) => handleInputChange('variables', { 
              ...inputs.variables, 
              independent: e.target.value 
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="Independent Variable: What you manipulate"
            disabled={isSubmitted}
          />
          <input
            type="text"
            value={inputs.variables?.dependent || ''}
            onChange={(e) => handleInputChange('variables', { 
              ...inputs.variables, 
              dependent: e.target.value 
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            placeholder="Dependent Variable: What you measure"
            disabled={isSubmitted}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Methodology *
        </label>
        <textarea
          value={inputs.methodology || ''}
          onChange={(e) => handleInputChange('methodology', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={4}
          placeholder="Describe your experimental design, sample size, procedures, and controls"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ethical Considerations *
        </label>
        <textarea
          value={inputs.ethics || ''}
          onChange={(e) => handleInputChange('ethics', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="What ethical issues must you consider? How will you address them?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Expected Results
        </label>
        <textarea
          value={inputs.expectedResults || ''}
          onChange={(e) => handleInputChange('expectedResults', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="What results do you expect and why?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  const renderPolicyAssessment = () => (
    <div className="space-y-6">
      {scenario?.policyDescription && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-medium mb-2">Policy Overview:</h4>
          <div className="text-sm text-gray-700">
            {scenario.policyDescription}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Policy Goals *
        </label>
        <textarea
          value={inputs.policyGoals || ''}
          onChange={(e) => handleInputChange('policyGoals', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="What are the stated goals and objectives of this policy?"
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Target Groups
        </label>
        {[0, 1, 2].map((index) => (
          <input
            key={index}
            type="text"
            value={inputs.targetGroups?.[index] || ''}
            onChange={(e) => {
              const newGroups = [...(inputs.targetGroups || ['', '', ''])];
              newGroups[index] = e.target.value;
              handleInputChange('targetGroups', newGroups);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
            placeholder={`Target Group ${index + 1}: Who does this policy aim to help?`}
            disabled={isSubmitted}
          />
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Positive Impacts
          </label>
          {[0, 1, 2].map((index) => (
            <textarea
              key={index}
              value={inputs.impacts?.positive?.[index] || ''}
              onChange={(e) => {
                const newImpacts = { ...inputs.impacts };
                if (!newImpacts.positive) newImpacts.positive = ['', '', ''];
                newImpacts.positive[index] = e.target.value;
                handleInputChange('impacts', newImpacts);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
              rows={2}
              placeholder={`Positive Impact ${index + 1}`}
              disabled={isSubmitted}
            />
          ))}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Negative Impacts
          </label>
          {[0, 1, 2].map((index) => (
            <textarea
              key={index}
              value={inputs.impacts?.negative?.[index] || ''}
              onChange={(e) => {
                const newImpacts = { ...inputs.impacts };
                if (!newImpacts.negative) newImpacts.negative = ['', '', ''];
                newImpacts.negative[index] = e.target.value;
                handleInputChange('impacts', newImpacts);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
              rows={2}
              placeholder={`Negative Impact ${index + 1}`}
              disabled={isSubmitted}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Effectiveness Assessment *
        </label>
        <textarea
          value={inputs.effectiveness || ''}
          onChange={(e) => handleInputChange('effectiveness', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="How effective has this policy been in achieving its goals? Provide evidence."
          disabled={isSubmitted}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Proposed Improvements
        </label>
        <textarea
          value={inputs.improvements || ''}
          onChange={(e) => handleInputChange('improvements', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="What changes would you recommend to improve this policy?"
          disabled={isSubmitted}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {getExerciseIcon()}
            <div className="ml-3">
              <h3 className="text-xl font-bold">{content.title}</h3>
              <p className="text-blue-100 text-sm">{content.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {/* Focus indicator */}
            <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
              windowFocused ? 'bg-green-500 bg-opacity-20 text-green-100' : 'bg-red-500 bg-opacity-20 text-red-100'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${
                windowFocused ? 'bg-green-300' : 'bg-red-300'
              }`}></div>
              {windowFocused ? 'Focused' : 'Not Focused'}
            </div>
            
            <div className="text-sm">
              Time: {formatTime(timeSpent)}
            </div>
            
            {isEngaged && (
              <div className="flex items-center text-xs px-2 py-1 rounded-full bg-green-500 bg-opacity-20 text-green-100">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                Engaged
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <p className="text-gray-700">{instructions}</p>
        
        {/* Engagement progress */}
        {!isEngaged && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
              <span>Minimum research time</span>
              <span>{formatTime(timeSpent)} / {formatTime(minimumEngagementTime)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min((timeSpent / minimumEngagementTime) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Exercise Content */}
      <div className="p-6">
        {renderExerciseContent()}

        {/* Hints Section */}
        {hints && hints.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setShowHints(!showHints)}
              className="flex items-center text-blue-600 hover:text-blue-700 mb-3"
            >
              <LightBulbIcon className="h-4 w-4 mr-2" />
              {showHints ? 'Hide Hints' : 'Show Hints'}
            </button>
            
            {showHints && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">
                    Hint {currentHint + 1} of {hints.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentHint(Math.max(0, currentHint - 1))}
                      disabled={currentHint === 0}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentHint(Math.min(hints.length - 1, currentHint + 1))}
                      disabled={currentHint === hints.length - 1}
                      className="text-xs px-2 py-1 bg-yellow-200 text-yellow-800 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <p className="text-yellow-700 text-sm">{hints[currentHint]}</p>
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div className="mt-6">
          {!isSubmitted ? (
            <button
              onClick={handleSubmit}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Submit Research
            </button>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                {isCorrect ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-2" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-2" />
                )}
                <span className="font-medium">
                  Score: {results.score}% {isCorrect ? '(Passed)' : '(Review Required)'}
                </span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {results.feedback}
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Time spent: {formatTime(results.timeSpent)} | Interactions: {results.interactions}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialSciencesInteractive;